import { DateTime } from 'luxon';

import DatadogApi from '@/common/apis/DatadogApi';
import EntityProperty from '@/common/models/EntityProperty';
import { getEntityProperties } from '@/common/models/geo/GeofenceGroup.entityProperties';
import GeofenceGroupType from '@/common/models/geofenceGroups/GeofenceGroupType.enum';
import { DASH, toServerDateTime } from '@/globals';
import { getDiff } from '@/utils/Diff';
import Validatable, { IEntityChange } from '@/validator/Validatable';

import Geofence, { IGeofenceAPI } from './Geofence';

export interface IGeofenceGroupAPI {
  geofencegroup_id: number;
  name: string;
  description: string;
  tags: string[];
  organization_id?: number;
  geofences: IGeofenceAPI[];
  created_by: number;
  created_on_utc?: string;
  modified_by: number;
  modified_on_utc?: string;
  last_refreshed_on?: { last_refreshed_on: string } | null;
  usage_id: number;
}

export default class GeofenceGroup extends Validatable {
  static entityPropertiesCached: Record<string, EntityProperty<GeofenceGroup>> = null;

  apiObject: IGeofenceGroupAPI;
  cachedOriginalObject: GeofenceGroup;

  name: string;
  description?: string;
  tags: string[];
  organizationID: number;
  createdOnUtc?: DateTime;
  createdBy: number;
  modifiedOnUtc?: DateTime;
  modifiedBy: number;
  geofences?: Geofence[];
  usageID: number;
  lastRefreshedOnUtc?: DateTime;
  timestamp?: DateTime;

  constructor(
    id = -1,
    name = '',
    description = '',
    tags?: string[],
    organizationID?: number,
    createdOnUtc?: DateTime,
    createdBy?: number,
    modifiedOnUtc?: DateTime,
    modifiedBy?: number,
    geofences: Geofence[] = [],
    usageID?: number,
    lastRefreshedOnUtc?: DateTime,
  ) {
    super();
    this.id = id;
    this.name = name;
    this.description = description;
    this.tags = tags;
    this.organizationID = organizationID;
    this.geofences = geofences;
    this.createdOnUtc = createdOnUtc;
    this.createdBy = createdBy;
    this.modifiedOnUtc = modifiedOnUtc;
    this.modifiedBy = modifiedBy;
    this.usageID = usageID;
    this.lastRefreshedOnUtc = lastRefreshedOnUtc;

    this.timestamp = DateTime.local();
  }

  get originalObject(): GeofenceGroup {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = GeofenceGroup.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  get endpoint() {
    return this.usageID === GeofenceGroupType.AUDIENCE
      ? `/audienceGeofenceGroups/${this.id}`
      : `/proximityGeofenceGroups/${this.id}`;
  }

  get displayName() {
    return this.name;
  }

  get geofencesCount() {
    return this.geofences.length;
  }

  get createdOnFormatted() {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted() {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get features() {
    return this.geofences.map((g) => g.feature);
  }

  get isReady() {
    if (!this.lastRefreshedOnUtc) {
      return false;
    }

    if (!this.modifiedOnUtc) {
      return this.createdOnUtc < this.lastRefreshedOnUtc;
    }

    return this.modifiedOnUtc < this.lastRefreshedOnUtc;
  }

  clone(isDuplicatingExistingGeofence) {
    const apiModel = this.toApi();
    apiModel.geofencegroup_id = -1;
    if (!isDuplicatingExistingGeofence) {
      apiModel.name = `Copy of ${apiModel.name}`;
    }
    return GeofenceGroup.fromApi(apiModel);
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = ['created_by', 'modified_by', 'archived', 'last_refreshed_on', 'organization_id'];
    const diffs = getDiff(
      this.apiObject,
      this.toApi(true),
      ignoreProperties,
      [],
      [],
      [{ key: 'dataprovider_id', values: [null] }],
    );
    if (diffs.length === 0) return true;
    DatadogApi.logUnexpectedChange(diffs);
    return false;
  }

  async getChanges(): Promise<IEntityChange<GeofenceGroup>[]> {
    if (this.isNew) return [];

    return this.getValidatableChanges<GeofenceGroup>(this.originalObject, GeofenceGroup.entityProperties);
  }

  static fromObject(obj) {
    return super.fromObject(obj, new GeofenceGroup());
  }

  static fromApi(apiObject: IGeofenceGroupAPI) {
    const geofences = apiObject.geofences != null ? apiObject.geofences.map((x) => Geofence.fromApi(x)) : [];

    const geofenceGroup = GeofenceGroup.fromObject({
      id: apiObject.geofencegroup_id,
      name: apiObject.name,
      description: apiObject.description,
      tags: apiObject.tags,
      organizationID: apiObject.organization_id,
      geofences,
      apiObject,
      createdOnUtc:
        apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      modifiedOnUtc:
        apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      // The last_refreshed_on property is strangley nested inside another last_refreshed_on object, I don't know why ¯\_(ツ)_/¯
      lastRefreshedOnUtc:
        apiObject.last_refreshed_on?.last_refreshed_on != null
          ? DateTime.fromISO(apiObject.last_refreshed_on?.last_refreshed_on, { zone: 'utc' })
          : null,
      usageID: apiObject.usage_id != null ? apiObject.usage_id : GeofenceGroupType.PROXIMITY,
    });
    geofenceGroup.apiObject = apiObject;
    return geofenceGroup;
  }

  toApi(includeGeofences = false): IGeofenceGroupAPI {
    return {
      geofencegroup_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      description: this.description,
      tags: this.tags,
      created_on_utc: toServerDateTime(this.createdOnUtc),
      created_by: this.createdBy,
      modified_on_utc: toServerDateTime(this.modifiedOnUtc),
      modified_by: this.modifiedBy,
      geofences: includeGeofences ? this.geofences.map((g) => g.toApi()) : undefined,
      usage_id: this.usageID,
    };
  }

  static validatorCreateRules() {
    return {
      name: 'required',
      usageID: 'required',
    };
  }

  static get entityProperties() {
    if (GeofenceGroup.entityPropertiesCached != null) {
      return GeofenceGroup.entityPropertiesCached;
    }
    GeofenceGroup.entityPropertiesCached = getEntityProperties();
    return GeofenceGroup.entityPropertiesCached;
  }
}
